import React, { useEffect, useState } from 'react';
import { FaSpinner, FaMapMarkedAlt, FaMagic } from 'react-icons/fa';
import { getPublicTrip, trackEvent } from '../api/client';
import { DayCardsGrid, extractDaysFromContent } from './dashboard';
import BookingBlock from './BookingBlock';
import AffiliateDisclosure from './AffiliateDisclosure';
import { setPendingTripContext } from '../utils/tripContext';

/**
 * A published trip, read-only, for anyone with the link.
 *
 * This is the growth loop: a real plan is a far better advertisement than any
 * description of one, and until now a shared link was a dead end that required
 * the recipient to register before they could see anything. Here they see the
 * whole trip immediately, can book from it, and are invited to make their own.
 *
 * It renders through the same DayCardsGrid the owner sees, so the per-night
 * hotel buttons and their dates come for free rather than being reimplemented.
 */
const PublicTripView = ({ token, onGetStarted }) => {
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getPublicTrip(token)
      .then((data) => {
        if (cancelled) return;
        setTrip(data);
        trackEvent('public_trip_viewed', { destination: data.end_location });
      })
      .catch(() => {
        if (!cancelled) setError('This trip is no longer available.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handlePlanOwn = () => {
    if (trip) {
      // Carry the trip they liked into the planner, so "one like this" starts
      // from the same place and dates rather than an empty form.
      setPendingTripContext({
        destination: trip.end_location,
        origin: trip.start_location,
        startDate: trip.start_date,
        endDate: trip.end_date,
        source: 'public_trip',
      });
    }
    trackEvent('public_trip_plan_own_clicked', { destination: trip?.end_location });
    if (onGetStarted) onGetStarted(trip?.end_location);
  };

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <FaSpinner className="animate-spin text-3xl text-primary-500" />
      </div>
    );
  }

  if (error || !trip) {
    return (
      <div className="mx-auto flex min-h-[50vh] max-w-lg flex-col items-center justify-center px-6 text-center">
        <FaMapMarkedAlt className="mb-4 text-4xl text-neutral-300" />
        <h1 className="font-serif text-2xl font-bold text-neutral-900">Trip not available</h1>
        <p className="mt-2 text-neutral-600">
          {error || 'The owner may have withdrawn this link.'}
        </p>
        <button
          onClick={handlePlanOwn}
          className="mt-6 rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-700"
        >
          Plan your own trip free
        </button>
      </div>
    );
  }

  let days = [];
  try {
    days = trip.itinerary ? extractDaysFromContent(trip.itinerary) : [];
  } catch {
    days = [];
  }

  const nights =
    trip.start_date && trip.end_date
      ? Math.max(
          1,
          Math.round(
            (new Date(`${trip.end_date}T00:00:00`) - new Date(`${trip.start_date}T00:00:00`)) /
              86400000
          )
        )
      : null;

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      <header className="mb-8">
        <p className="text-xs font-semibold uppercase tracking-wide text-neutral-400">
          A trip planned with ItinerarySeek
        </p>
        <h1 className="mt-1 font-serif text-3xl font-bold leading-tight text-neutral-900 sm:text-4xl">
          {trip.end_location}
        </h1>
        <p className="mt-2 text-neutral-600">
          {nights ? `${nights + 1} days` : 'Day by day'}
          {trip.start_date ? ` from ${trip.start_date}` : ''}
          {trip.start_location && trip.start_location !== trip.end_location
            ? ` · starting in ${trip.start_location}`
            : ''}
        </p>

        <button
          onClick={handlePlanOwn}
          className="mt-5 inline-flex items-center gap-2 rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700"
        >
          <FaMagic size={13} /> Plan your own like this — free
        </button>
      </header>

      {days.length > 0 ? (
        <DayCardsGrid
          days={days}
          startDate={trip.start_date}
          weatherData={null}
          attractionStatuses={{}}
          onViewDay={() => {}}
          isSwapping={false}
          isOwner={false}
          userTier="individual"
          onViewPricing={handlePlanOwn}
          destination={trip.end_location}
          endDate={trip.end_date}
          transportationMode={trip.transportation_mode}
          bookingSurface={`public_${token.slice(0, 12)}`}
        />
      ) : (
        <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-8 text-center text-neutral-500">
          This trip has no day-by-day plan to show.
        </div>
      )}

      <BookingBlock
        destination={trip.end_location}
        surface={`public_${token.slice(0, 12)}`}
        initialCheckIn={trip.start_date}
        initialCheckOut={trip.end_date}
        initialOrigin={trip.start_location}
        heading={`Where to stay in ${trip.end_location}`}
      />

      <div className="my-10 rounded-xl border border-primary-200 bg-gradient-to-r from-primary-50 to-transparent px-6 py-6 text-center">
        <h2 className="font-serif text-xl font-bold text-neutral-900">
          Want one of these for your own trip?
        </h2>
        <p className="mx-auto mt-2 max-w-xl text-neutral-600">
          Tell us where you're going and we'll build the whole thing — the stops, the
          timings, the route and where to stay each night.
        </p>
        <button
          onClick={handlePlanOwn}
          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700"
        >
          <FaMagic size={13} /> Plan my trip free
        </button>
      </div>

      <AffiliateDisclosure />
    </div>
  );
};

export default PublicTripView;
